// Seasonal / limited-time collection UI. Renders the server's
// availabilityStatus — it never decides reward eligibility itself.
// Permanent collections render nothing, so they look exactly as before.
import React from 'react';
import {
    AVAILABILITY_STATUS,
    formatSeasonEnd,
    formatSeasonRange,
    seasonalBadge,
    seasonCountdownLabel,
} from '@aluvo/constants/src/collectionAvailability';
import styles from './SeasonalAvailability.module.css';

const STATUS_CLASS = {
    [AVAILABILITY_STATUS.ACTIVE]: styles.active,
    [AVAILABILITY_STATUS.UPCOMING]: styles.upcoming,
    [AVAILABILITY_STATUS.ENDED]: styles.ended,
    owned: styles.ownedBadge, // an earned limited edition (history, not an expired challenge)
};

function formatEarnedDate(value) {
    const date = value ? new Date(value) : null;
    if (!date || Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

/** Compact pill for collection cards: "LIMITED TIME · Oct 1 – Oct 31". */
export function SeasonalBadge({ collection }) {
    const badge = seasonalBadge(collection);
    if (!badge) return null;
    return (
        <span className={`${styles.badge} ${STATUS_CLASS[badge.status] || ''}`} data-testid="seasonal-badge">
            <span className={styles.badgeLabel}>
                {badge.status === AVAILABILITY_STATUS.ACTIVE ? '🎃 ' : ''}{badge.label}
            </span>
            {badge.detail && <span className={styles.badgeDetail}>{badge.detail}</span>}
        </span>
    );
}

/** Prominent block for the collection detail page. */
export function SeasonalBanner({ collection }) {
    if (!collection?.isSeasonal) return null;
    const { availabilityStatus: status, availableFrom, availableUntil, rewardOwned, earnedAt } = collection;
    // Owners see the label frozen onto their coin when they earned it.
    const seasonLabel = (rewardOwned && collection.ownedSeasonLabel) || collection.seasonLabel;
    const earned = rewardOwned ? formatEarnedDate(earnedAt) : null;

    let title;
    let lines = [];
    if (status === AVAILABILITY_STATUS.ACTIVE) {
        title = 'LIMITED-TIME COLLECTION';
        lines = [
            `Available until ${formatSeasonEnd(availableUntil)}`,
            !rewardOwned && seasonCountdownLabel(availableUntil),
        ];
    } else if (status === AVAILABILITY_STATUS.UPCOMING) {
        title = 'COMING SOON';
        lines = [
            `Available ${formatSeasonRange(availableFrom, availableUntil)}`,
            'Titles you verify now count toward it. The coin unlocks once the season starts.',
        ];
    } else if (status === AVAILABILITY_STATUS.ENDED) {
        title = rewardOwned ? 'LIMITED EDITION · SEASON ENDED' : 'SEASON ENDED';
        lines = [
            formatSeasonRange(availableFrom, availableUntil),
            !rewardOwned && 'This collection coin is no longer available to earn.',
        ];
    } else {
        return null;
    }

    return (
        <div className={`${styles.banner} ${STATUS_CLASS[status] || ''}`} data-testid="seasonal-banner">
            <div className={styles.bannerTitle}>{title}</div>
            {seasonLabel && <div className={styles.bannerLabel}>{seasonLabel}</div>}
            {lines.filter(Boolean).map((line) => (
                <div key={line} className={styles.bannerLine}>{line}</div>
            ))}
            {rewardOwned && (
                <div className={styles.owned}>
                    ✓ OWNED{earned ? ` · Earned ${earned}` : ''}
                </div>
            )}
        </div>
    );
}

/**
 * Card / detail copy replacing "N more to unlock" when the reward can't be
 * earned right now. Returns null when the normal copy applies.
 */
export function seasonalProgressCopy(collection) {
    if (!collection?.isSeasonal || collection.completed || collection.rewardOwned) return null;
    if (collection.availabilityStatus === AVAILABILITY_STATUS.ENDED) return 'Reward no longer obtainable';
    if (collection.availabilityStatus === AVAILABILITY_STATUS.UPCOMING) {
        return collection.remaining === 0 ? 'Ready — unlocks when the season starts' : `${collection.remaining} more · unlocks when the season starts`;
    }
    return null;
}

export default SeasonalBanner;
